"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// Conditional import for dev tools (only in development)
import { useState, useEffect } from "react";
import { setupConsoleOverrides } from "@/lib/utils/console-override";
import { TooltipProvider } from "@/components/ui/tooltip";
import { restorePersistenceOnLogin } from "@/lib/persistence-utils";
import { getReactQueryDefaults } from "@/lib/cache-policy";
import { setQueryClient } from "@/lib/query-client-registry";

// Global cache configuration for all API calls
const createQueryClient = () => {
  const rq = getReactQueryDefaults();
  // Shorter staleTime (30 seconds) for faster perceived updates
  const staleTime = rq.enabled ? 30 * 1000 : 0; // 30 seconds
  // Keep data in cache longer (5 minutes) for instant back navigation
  const gcTime = rq.enabled ? 5 * 60 * 1000 : 0; // 5 minutes

  return new QueryClient({
  defaultOptions: {
    queries: {
      // Derived from global cache policy
      staleTime,
      gcTime,
      // Retry failed requests once
      retry: 1,
      // Don't refetch on window focus (prevents unnecessary calls)
      refetchOnWindowFocus: false,
      // Refetch stale data on mount for fresh content on navigation
      refetchOnMount: 'always',
      // Refetch stale data on reconnect
      refetchOnReconnect: 'always',
      // Enable network mode for better offline handling
      networkMode: 'online',
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
      // Network mode for mutations
      networkMode: 'online',
    },
  },
  });
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => {
    const client = createQueryClient();
    // Expose the mounted client to non-React modules (axios/fetch interceptors)
    // so they can invalidate queries after any successful write.
    setQueryClient(client);
    return client;
  });

  // Set up console overrides and global caching when the app loads
  useEffect(() => {
    setupConsoleOverrides();
    
    // Restore persistence data on app initialization
    restorePersistenceOnLogin();
    
    // Initialize global API cache interceptor on client, then the auth-refresh
    // interceptor. Order matters: the auth interceptor must be installed AFTER
    // the cache one so it wraps outermost and sees real network status codes.
    import("@/lib/api-cache-interceptor").then(() => {
      if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
        // eslint-disable-next-line no-console
        console.log('[CACHE] Global API cache interceptor initialized');
      }
      return import("@/lib/api-auth-interceptor");
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {/*
        PlaidLinkProvider removed: nothing consumed usePlaidLinkContext(), so this
        globally-mounted provider only spun up a redundant Plaid Link SDK instance on
        every page. Combined with the per-form usePlaidLink() hook (hooks/use-plaid-link.ts,
        still the real integration), that produced Plaid's "link-initialize.js embedded more
        than once" warning. Bank linking is unchanged - forms use the hook directly.
      */}
      <TooltipProvider>
        {children}
      </TooltipProvider>
      {/* DevTools will be available if package is installed */}
    </QueryClientProvider>
  );
}
